import type {
    AnatomyBinding,
    AnatomyLiteralBinding,
    AnatomySize,
    AnatomyTokenBinding,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

const boxSize = (pixels: string): readonly AnatomyBinding[] => [
    {
        partId: "background",
        property: "width",
        literal: pixels,
        via: "--avatar-size",
        note: "Figma geometry, no token family.",
    },
    {
        partId: "background",
        property: "height",
        literal: pixels,
        via: "--avatar-size",
    },
];

type AnatomyValue =
    | Pick<AnatomyTokenBinding, "token" | "literal">
    | Pick<AnatomyLiteralBinding, "token" | "literal">;

const oldIconListOverlap = {
    token: "--space-3",
    note: "ds-deviation: the old IconList overlap (spec Geometry).",
} as const;

const oldIconListSeam = {
    literal: "1.5px",
    note: "ds-deviation: the old IconList's 1.5px white ring, transparent here so stacks read on any surface (spec A7).",
} as const;

/** Group geometry per group size; `radius` is half the avatar, the round cut's radius. */
export const avatarGroupAnatomySizes = [
    {
        id: "xs",
        radius: "8px",
        overlap: {
            token: "--space-2",
            note: "Figma EntityGroup mr -2px: at 16px the old 3px hid too much.",
        },
        seam: {
            token: "--border-width",
            note: "Figma's 1px ring width, transparent and outside the next avatar.",
        },
        diamondOverlap: { literal: "0px" },
        chipHeight: "16px",
        chipRadius: "--radius-s",
        chipText: anatomyTextStyle({
            scale: "2xs",
            weight: "500",
            mixin: "text-2xs-10-medium",
            partId: "countChip",
        }),
    },
    {
        id: "sm",
        radius: "10px",
        overlap: oldIconListOverlap,
        seam: oldIconListSeam,
        diamondOverlap: oldIconListOverlap,
        chipHeight: "20px",
        chipRadius: "--radius-s",
        chipText: anatomyTextStyle({
            scale: "2xs",
            weight: "500",
            mixin: "text-2xs-10-medium",
            partId: "countChip",
        }),
    },
    {
        id: "md",
        radius: "12px",
        overlap: oldIconListOverlap,
        seam: oldIconListSeam,
        diamondOverlap: oldIconListOverlap,
        chipHeight: "24px",
        chipRadius: "--radius-m",
        chipText: anatomyTextStyle({
            scale: "xs",
            weight: "500",
            mixin: "text-xs-12-medium",
            partId: "countChip",
        }),
    },
] as const satisfies ReadonlyArray<{
    id: string;
    radius: string;
    overlap: AnatomyValue & { note: string };
    seam: AnatomyValue & { note: string };
    /** Class stacks don't overlap at 16px, where letters reach the tips. */
    diamondOverlap: AnatomyValue;
    chipHeight: string;
    chipRadius: `--${string}`;
    chipText: readonly AnatomyBinding[];
}>;

const groupBindings = (
    groupSize: (typeof avatarGroupAnatomySizes)[number],
): readonly AnatomyBinding[] => [
    {
        partId: "seam",
        property: "overlap",
        ...groupSize.overlap,
        via: "--avatar-group-overlap",
    },
    {
        partId: "seam",
        property: "seam",
        ...groupSize.seam,
        via: "--avatar-group-seam",
    },
    {
        partId: "seam",
        property: "cut radius",
        literal: groupSize.radius,
        via: "--avatar-group-r",
        note: "Half the avatar: the round cut's radius, and the base of the diamond edge.",
    },
    {
        partId: "countChip",
        property: "gap",
        token: "--space-4",
        note: "Between the stack and the chip (Figma gap 4).",
    },
    {
        partId: "countChip",
        property: "background-color",
        token: "--bg-neutral",
        via: "--tag-bg",
        note: `Tag size="${groupSize.id}" numeric color="primary"; its size follows the group.`,
    },
    {
        partId: "countChip",
        property: "color",
        token: "--fg-primary",
        via: "--tag-fg",
    },
    { partId: "countChip", property: "height", literal: groupSize.chipHeight },
    {
        partId: "countChip",
        property: "border-radius",
        token: groupSize.chipRadius,
    },
    ...groupSize.chipText,
];

const findGroupSize = (id: string) =>
    avatarGroupAnatomySizes.find((groupSize) => groupSize.id === id);

const withGroup = (id: string): readonly AnatomyBinding[] => {
    const groupSize = findGroupSize(id);
    return groupSize === undefined ? [] : groupBindings(groupSize);
};

export const avatarAnatomySizes: readonly AnatomySize[] = [
    {
        id: "xs",
        label: "xs",
        selector: '[data-size="xs"]',
        bindings: [
            ...boxSize("16px"),
            ...anatomyTextStyle({
                scale: "3xs",
                weight: "500",
                mixin: "text-3xs-8-medium",
            }),
            ...withGroup("xs"),
        ],
    },
    {
        id: "sm",
        label: "sm",
        selector: '[data-size="sm"]',
        bindings: [
            ...boxSize("20px"),
            ...anatomyTextStyle({
                scale: "2xs",
                weight: "500",
                mixin: "text-2xs-10-medium",
            }),
            ...withGroup("sm"),
        ],
    },
    {
        id: "md",
        label: "md",
        selector: '[data-size="md"]',
        bindings: [
            ...boxSize("24px"),
            ...anatomyTextStyle({
                scale: "2xs",
                weight: "500",
                mixin: "text-2xs-10-medium",
            }),
            ...withGroup("md"),
        ],
    },
    {
        id: "lg",
        label: "lg",
        selector: '[data-size="lg"]',
        bindings: [
            ...boxSize("32px"),
            ...anatomyTextStyle({
                scale: "xs",
                weight: "500",
                mixin: "text-xs-12-medium",
                note: "Not drawn in Figma (spec A4).",
            }),
        ],
    },
];

/** Mirrors `--avatar-diamond-outer`: the side of a diamond whose tips touch a `size` box. */
export const diamondOuterSide = (size: string) =>
    `calc((${size} + var(--radius-xs) * 0.8284) * 0.7071)`;
